import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, catchError, finalize, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { AppConfiguaration } from 'assets/app.configuaration';
import { MasterServiceService } from '../service/master-service.service';
import { EncryptedStorageService } from '../service/EncryptedStorage.service';
import { CMSStorageService } from '../service/CMS-Storage.service';
import { ENDPOINT_UTILS } from '../utils/end-point.utils';
import { UserService } from '../service/user.service';

const SKIP_HEADER = 'X-Skip-Interceptor';
const API_PREFIX = 'api://';

@Injectable({
  providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor {

  constructor(
    private http: HttpClient,
    private config: AppConfiguaration,
    private toaster: ToastrService,
    private readonly router: Router,
    private readonly loader: MasterServiceService,
    private readonly secureStore: EncryptedStorageService,
    private readonly cmsStore: CMSStorageService,
    private readonly userService: UserService
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isAsset(req.url)) {
      return next.handle(req);
    }

    this.loader.setLoading(true);

    const modifiedReq = this.prepareRequest(req);

    return next.handle(modifiedReq).pipe(
      catchError((err) => this.handleError(err, modifiedReq, next)),
      finalize(() => this.loader.setLoading(false))
    );
  }

  // ---------------------------
  //  REQUEST HELPERS
  // ---------------------------

  private prepareRequest(req: HttpRequest<any>): HttpRequest<any> {
    const url = this.resolveApiUrl(req.url);
    let headers = req.headers.delete(SKIP_HEADER);

    const token = this.secureStore.getItem('Authorization');
    if (token) headers = headers.set('Authorization', token);

    const shouldRemoveContentType = this.shouldRemoveContentType(url);
    if (!shouldRemoveContentType) {
      headers = headers
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json');
    } else {
      headers = headers.delete('Content-Type').delete('Accept');
    }

    return req.clone({ url, headers });
  }

  private resolveApiUrl(url: string): string {
    if (url.startsWith(API_PREFIX)) {
      const apiBase = this.config.getConfig('apiUrl') ?? 'https://';
      return url.replace(API_PREFIX, apiBase);
    }
    return url;
  }

  private shouldRemoveContentType(url: string): boolean {
    const specialEndpoints = [
      'reimbursement/saveReimbursementBillCopy',
      'reimbursement/saveReimbursementCertificateCopy',
      'accounting/saveExpenseBillDoc',
      'accounting/saveInvoiceDoc',
      'updateDefect',
      'createClient',
      'clientProfile',
      'defect/createDefect',
      'employeeDocumentUpload',
      'punch',
      'saveProjectsDocuments',
      'employee/saveProfile',
      'employee/updateEmployeeProfileImage',
      'admin/save/employeeOnsite',
      'employee/saveEmployee'

    ];
    return specialEndpoints.some((x) => url.split('?')[0].endsWith(x));
  }

  private isAsset(url: string): boolean {
    return (
      url.includes('/assets/') ||
      url.endsWith('.json') ||
      url.endsWith('.css') ||
      url.endsWith('.js') ||
      url.endsWith('.png') ||
      url.endsWith('.jpg') ||
      url.endsWith('.jpeg') ||
      url.endsWith('.gif') ||
      url.endsWith('.svg') ||
      url.endsWith('.woff') ||
      url.endsWith('.woff2') ||
      url.endsWith('.ttf') ||
      url.endsWith('.eot')
    );
  }

  // ---------------------------
  //  ERROR HANDLING
  // ---------------------------

  private handleError(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {

    // --------------------------
    // 500 - SERVER ERROR
    // --------------------------
    if (error.status === 500) {
      this.toaster.error(
        error.error?.statusMessage ??
        error.error?.status ??
        error.error?.error ??
        'Server Error'
      );
      return throwError(() => error);
    }

    // --------------------------
    // 503 - SERVER DOWN
    // --------------------------
    if (error.status === 503) {
      this.toaster.error('503 Service Temporarily Unavailable');
      return throwError(() => error);
    }

    // --------------------------
    // 400 - BAD REQUEST
    // --------------------------
    if (error.status === 400) {
      this.toaster.error(error.error?.errors?.[0] ?? 'Invalid request');
      return throwError(() => error);
    }

    // --------------------------
    // 401 - UNAUTHORIZED (REFRESH LOGIC)
    // --------------------------
    if (error.status === 401) {

      if (req.url.endsWith('auth/refreshToken')) {
        this.router.navigate(['/sign-in']);
        return throwError(() => error);
      }

      const refreshToken = this.secureStore.getItem('RefreshToken');
      const userId = this.secureStore.getItem('UserId');

      return this.http.post(ENDPOINT_UTILS.auth.refreshToken, {
        refreshToken,
        userId
      }).pipe(
        switchMap((res: any) => {

          if (res.status !== 'Success') {
            this.router.navigate(['/sign-in']);
            return throwError(() => error);
          }

          // SAVE TOKENS
          const access = res.data.accessToken;
          const refresh = res.data.refreshToken;

          this.secureStore.setItem('Authorization', `Bearer ${access}`);
          this.secureStore.setItem('RefreshToken', refresh);
          this.userService.loadUserProfile();
          // REPEAT ORIGINAL CALL
          const newReq = req.clone({
            setHeaders: { Authorization: `Bearer ${access}` }
          });

          return next.handle(newReq);
        }),
        catchError(() => {
          this.router.navigate(['/sign-in']);
          return throwError(() => error);
        })
      );
    }

    return throwError(() => error);
  }
}
